import sharedStateBridge, {
  composeTabs,
  COMPOSE_TABS,
  defaultCompose,
  ComposeStore,
  MediaAttachedType,
  MediaIdType,
  MediaDataForSaving,
} from '@sendible/shared-state-bridge';
import { publish, EVENTS } from '@sendible/event-manager';

const clearComposeBoxState = () => {
  publish(EVENTS.CANCEL_ALL_UPLOADS_AND_CLOSE_BU, {});

  const sharedState = sharedStateBridge.getSharedState();

  sharedStateBridge.setItem({
    ...sharedState,
    compose: { ...defaultCompose },
  });
};

const getAttachedMedia = (tabId: composeTabs | undefined): MediaAttachedType | undefined => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { activeComposeBoxTab, tabsState } = composeState;

  return tabsState && tabsState[tabId || activeComposeBoxTab]?.mediaAttached;
};

const setActiveComposeBoxTab = (tabId: composeTabs | undefined) => sharedStateBridge.updateSpecificKey('compose', 'activeComposeBoxTab', tabId);

const setIsComposeBoxOpen = (state: boolean) => sharedStateBridge.updateSpecificKey('compose', 'isComposeBoxOpen', state);

const setIsMediaAttacherOpen = (state: boolean) => sharedStateBridge.updateSpecificKey('compose', 'isMediaAttacherOpen', state);

const toggleMediaAttacher = () => {
  const currentMediaAttacherState = sharedStateBridge.getSharedState().compose.isMediaAttacherOpen || false;

  sharedStateBridge.updateSpecificKey('compose', 'isMediaAttacherOpen', !currentMediaAttacherState);
};

const updateNonCustomisedTabs = () => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState && composeState.tabsState[COMPOSE_TABS.MESSAGE]?.mediaAttached) {
    const originalTabMedias = composeState.tabsState[COMPOSE_TABS.MESSAGE]?.mediaAttached;
    const newTabsState = { ...composeState.tabsState };

    Object.entries(composeState.tabsState)
      .filter(([tabName, tabsState]) => tabName !== COMPOSE_TABS.MESSAGE && !tabsState.isCustomised)
      .forEach(([tabName, tabsState]) => {
        newTabsState[tabName as composeTabs] = {
          ...tabsState,
          mediaAttached: originalTabMedias,
        };
      });
    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
  }
};

const attachMedia = (mediaId: MediaIdType) => {
  setIsMediaAttacherOpen(false);

  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, activeComposeBoxTab } = composeState;
    const newTabsState = {
      ...tabsState,
      [activeComposeBoxTab]: {
        ...tabsState[activeComposeBoxTab],
        isCustomised: true,
        mediaAttached: { ...tabsState[activeComposeBoxTab]?.mediaAttached, [mediaId]: {} as MediaType },
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);

    if (activeComposeBoxTab === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }
    publish(EVENTS.MEDIA_ADDED, activeComposeBoxTab);
  }
};
const getIsUploadingMedias = () => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = composeState;

  return !!Object.values(tabsState)
    .map((tabState) => Object.values(tabState.mediaAttached || {}))
    .flat()
    .some((media) => media.status !== 'Ready');
};
const updateMediaProperties = (data: MediaType[]) => {
  setIsMediaAttacherOpen(false);

  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, activeComposeBoxTab } = composeState;

    sharedStateBridge.updateSpecificKey(
      'compose',
      'tabsState',
      data.reduce(
        (newTabsState, media) => ({
          ...newTabsState,
          [activeComposeBoxTab]: {
            ...newTabsState[activeComposeBoxTab],
            mediaAttached: { ...newTabsState[activeComposeBoxTab]?.mediaAttached, [media.id]: media },
          },
        }),
        tabsState
      )
    );
    if (activeComposeBoxTab === COMPOSE_TABS.MESSAGE) updateNonCustomisedTabs();
    publish(EVENTS.TRIGGER_VALIDATION, {});
  }
};

const setMajorErrorFeedback = (message: string) => {
  publish(EVENTS.ERROR_MESSAGE_FEEDBACK, {
    message,
  });
};

const getAllAttachedMediaIds = (): MediaIdType[] => {
  const compose: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = compose;

  const setOfIds = Object.values(tabsState).reduce((acc, tabState) => {
    if (tabState.mediaAttached) {
      Object.keys(tabState.mediaAttached).forEach((mediaId) => {
        acc.add(mediaId);
      });
    }

    return acc;
  }, new Set<MediaIdType>());

  return Array.from(setOfIds);
};

const getMediaDataForSavingInCompose = (tabId: composeTabs) => {
  const compose: ComposeStore = sharedStateBridge.getSharedState()?.compose;
  const { tabsState } = compose;
  const data: MediaDataForSaving = {
    tw_media: [],
    media: '',
  };

  if (!tabsState[tabId]?.mediaAttached) {
    return data;
  }

  const mediaAttached = Object.values(tabsState[tabId]?.mediaAttached || {});

  data.media = mediaAttached.reduce((acc, media) => {
    return `${acc}${media.name}|${media.originalUrl},`;
  }, '');

  data.tw_media = mediaAttached.map((media) => {
    return {
      id: media.id,
      type: media?.metadata?.mediaType || '',
    };
  });

  return data;
};

const setTabsState = (tabInfo: { [key: string]: { isCustomised?: boolean } }) => {
  const tabsState = sharedStateBridge.getSharedState()?.compose?.tabsState;

  const newTabsState = { ...tabsState };

  Object.entries(tabInfo).forEach(([tabName, tabInfoState]) => {
    // for each tab present in compose box
    newTabsState[tabName as composeTabs] = {
      ...tabsState[tabName], // whatever media we had previously attached
      ...tabInfoState, // whatever state comes from compose box
    };
  });
  sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);

  updateNonCustomisedTabs();
};

const removeMedia = (mediaIdToRemove: MediaIdType) => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, activeComposeBoxTab } = composeState;
    const newTabsState = {
      ...tabsState,
      [activeComposeBoxTab]: {
        ...tabsState[activeComposeBoxTab],
        isCustomised: true,
        mediaAttached: {
          ...tabsState[activeComposeBoxTab]?.mediaAttached,
          [mediaIdToRemove]: undefined,
        },
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);

    if (activeComposeBoxTab === COMPOSE_TABS.MESSAGE) {
      updateNonCustomisedTabs();
    }
    publish(EVENTS.MEDIA_REMOVED, activeComposeBoxTab);
  }
};

const removeAllMedia = () => {
  const composeState: ComposeStore = sharedStateBridge.getSharedState()?.compose;

  if (composeState?.tabsState) {
    const { tabsState, activeComposeBoxTab } = composeState;
    const newTabsState = {
      ...tabsState,
      [activeComposeBoxTab]: {
        ...tabsState[activeComposeBoxTab],
        mediaAttached: {},
      },
    };

    sharedStateBridge.updateSpecificKey('compose', 'tabsState', newTabsState);
    if (activeComposeBoxTab === COMPOSE_TABS.MESSAGE) updateNonCustomisedTabs();
  }
};

export const composeMediaInteractions = {
  updateMediaProperties,
  attachMedia,
  clearComposeBoxState,
  getAttachedMedia,
  getAllAttachedMediaIds,
  getMediaDataForSavingInCompose,
  removeAllMedia,
  removeMedia,
  setActiveComposeBoxTab,
  setIsComposeBoxOpen,
  setIsMediaAttacherOpen,
  setMajorErrorFeedback,
  setTabsState,
  toggleMediaAttacher,
  getIsUploadingMedias,
};
